// Synthesized branded musical stingers — each is ~1.5-3s of original audio.
// No external files, no copyrighted samples. Pure Web Audio API.

let ctx = null;
let activeNodes = [];

function getCtx() {
  if (ctx && ctx.state !== 'closed') return ctx;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  ctx = new AudioCtx();
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

function stopAll() {
  activeNodes.forEach((n) => {
    try { if (n.stop) n.stop(); } catch {}
    try { if (n.disconnect) n.disconnect(); } catch {}
  });
  activeNodes = [];
}

function noiseBuffer(c, sec = 1) {
  const buf = c.createBuffer(1, c.sampleRate * sec, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

function envGain(c, master, attack, decay, peak = 0.6, t0 = null) {
  const g = c.createGain();
  const t = t0 ?? c.currentTime;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.connect(master);
  return g;
}

function tone(c, master, freq, type, attack, decay, peak, t0 = null) {
  const t = t0 ?? c.currentTime;
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  const g = envGain(c, master, attack, decay, peak, t);
  o.connect(g);
  o.start(t);
  o.stop(t + attack + decay + 0.05);
  activeNodes.push(o);
  return { osc: o, gain: g };
}

function slideTone(c, master, fStart, fEnd, type, attack, decay, peak, t0 = null) {
  const t = t0 ?? c.currentTime;
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(fStart, t);
  o.frequency.exponentialRampToValueAtTime(fEnd, t + attack + decay);
  const g = envGain(c, master, attack, decay, peak, t);
  o.connect(g);
  o.start(t);
  o.stop(t + attack + decay + 0.05);
  activeNodes.push(o);
}

function noiseHit(c, master, fStart, fEnd, type, peak, dur, t0 = null) {
  const t = t0 ?? c.currentTime;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, dur + 0.2);
  const f = c.createBiquadFilter();
  f.type = type;
  f.frequency.setValueAtTime(fStart, t);
  f.frequency.exponentialRampToValueAtTime(fEnd, t + dur);
  f.Q.value = 1.4;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(f).connect(g).connect(master);
  src.start(t);
  src.stop(t + dur + 0.05);
  activeNodes.push(src);
}

function makeMaster(c, durationSec) {
  const m = c.createGain();
  m.gain.value = 0.5;
  m.connect(c.destination);
  const t = c.currentTime;
  // Auto fade-out at the end so re-triggers stay clean
  m.gain.setValueAtTime(0.5, t + durationSec - 0.3);
  m.gain.exponentialRampToValueAtTime(0.0001, t + durationSec);
  return m;
}

// ===== Brand stingers =====

function playNike(c) {
  // Athletic punch: kick + clap-snare + swoosh-up
  const m = makeMaster(c, 1.6);
  // Sub kick
  slideTone(c, m, 160, 38, 'sine', 0.01, 0.35, 0.95);
  // High percussive click
  noiseHit(c, m, 2500, 8000, 'highpass', 0.55, 0.08);
  // Swoosh noise sweep
  noiseHit(c, m, 200, 5500, 'bandpass', 0.45, 0.7);
  // Crisp clap a beat later
  setTimeout(() => {
    noiseHit(c, m, 1500, 6500, 'bandpass', 0.5, 0.12, c.currentTime);
  }, 200);
}

function playMercedes(c) {
  // Premium cinematic: bell + warm pad chord (luxury sting)
  const m = makeMaster(c, 2.4);
  // Soft bell hit
  const bellFreqs = [880, 1318.5, 1760]; // A5, E6, A6
  bellFreqs.forEach((f, i) => {
    tone(c, m, f, 'sine', 0.02, 1.6, 0.18 - i * 0.04);
  });
  // Warm pad chord: A minor (A3, C4, E4)
  setTimeout(() => {
    [220, 261.63, 329.63].forEach((f) => {
      tone(c, m, f, 'triangle', 0.5, 1.4, 0.12, c.currentTime);
    });
  }, 200);
}

function playComisario(c) {
  // Latin warm: wood block + warm pluck arpeggio (mariachi-ish, royalty-free vibe)
  const m = makeMaster(c, 2.2);
  // Wood block tick
  tone(c, m, 1200, 'square', 0.005, 0.05, 0.3);
  // Warm guitar-ish pluck arpeggio (D, F#, A — D major)
  const arpeg = [
    [293.66, 0],    // D4
    [369.99, 130],  // F#4
    [440, 260],     // A4
    [587.33, 390],  // D5
  ];
  arpeg.forEach(([f, delay]) => {
    setTimeout(() => {
      const t = c.currentTime;
      const o = c.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.32, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
      o.connect(g).connect(m);
      o.start(t);
      o.stop(t + 1);
      activeNodes.push(o);
    }, delay);
  });
}

function playJones(c) {
  // Playful bubbly: fizz + bouncy arpeggio (C major pentatonic)
  const m = makeMaster(c, 2.0);
  // Fizz noise
  noiseHit(c, m, 3000, 7000, 'highpass', 0.32, 1.2);
  // Bouncy notes: C5, E5, G5, C6, A5 (playful pentatonic skip)
  const notes = [
    [523.25, 0],
    [659.25, 90],
    [783.99, 180],
    [1046.5, 270],
    [880, 380],
  ];
  notes.forEach(([f, delay]) => {
    setTimeout(() => {
      tone(c, m, f, 'square', 0.005, 0.18, 0.18, c.currentTime);
    }, delay);
  });
}

function playDelta(c) {
  // Aviation lift: ascending chord swell with airy whoosh
  const m = makeMaster(c, 2.4);
  // Whoosh
  noiseHit(c, m, 300, 3500, 'bandpass', 0.38, 1.6);
  // Ascending arpeggio (Eb, G, Bb, Eb — Eb major triad)
  const notes = [
    [311.13, 0],
    [392.00, 140],
    [466.16, 280],
    [622.25, 420],
  ];
  notes.forEach(([f, delay]) => {
    setTimeout(() => {
      const t = c.currentTime;
      tone(c, m, f, 'triangle', 0.02, 0.8, 0.22, t);
    }, delay);
  });
  // Sustained pad
  setTimeout(() => {
    [155.56, 233.08, 311.13].forEach((f) => {
      tone(c, m, f, 'sine', 0.4, 1.4, 0.13, c.currentTime);
    });
  }, 400);
}

const STINGERS = {
  nike: playNike,
  mercedes: playMercedes,
  comisario: playComisario,
  jones: playJones,
  delta: playDelta,
};

let lastPlay = 0;

export function playBrandStinger(brandKey) {
  // Throttle: ignore re-triggers within 250ms to avoid mash spam
  const now = Date.now();
  if (now - lastPlay < 250) return;
  lastPlay = now;

  const c = getCtx();
  if (!c) return;
  stopAll();
  const fn = STINGERS[brandKey];
  if (fn) fn(c);
}

export function stopBrandStinger() {
  stopAll();
}
